import React, { RefObject } from 'react';

import { ASK_AI } from '@lumapps/ask-ai/keys';
import { useClassnames } from '@lumapps/classnames';
import { mdiArrowUp, mdiClose, mdiStop } from '@lumapps/lumx/icons';
import { Alignment, Button, ButtonProps, Emphasis, FlexBox, Orientation, Size } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useIsInView } from '@lumapps/utils/hooks/useIsInView';

import { ASK_AI_CONVERSATION } from '../../../keys';
import { AskAiFollowUpInput } from '../../AskAiFollowUpInput';

import './index.scss';

const CLASSNAME = 'ask-ai-follow-up-questions-footer';

interface AskAiFollowUpQuestionsFooterProps {
    /** Whether the follow-up questions should be displayed */
    isOpen?: boolean;
    /** Callback when the input is submitted */
    onSubmit: (value?: string) => void;
    /** Whether the answer is currently streaming or loading */
    isStreamingOrLoading?: boolean;
    /** Callback when the conversation should be closed  */
    onClose: () => void;
    /** Ref of the input field */
    inputRef: RefObject<HTMLInputElement>;
    isInitialResultStreaming?: boolean;
    /** Ref of the submit button */
    buttonRef: RefObject<HTMLButtonElement>;
    onStopStream: () => void;
}

/**
 * Footer of the ask ai follow up questions.
 * It holds the query input and close button
 */
export const AskAiFollowUpQuestionsFooter: React.FC<AskAiFollowUpQuestionsFooterProps> = ({
    isOpen,
    inputRef,
    buttonRef,
    onSubmit,
    isStreamingOrLoading,
    onClose,
    isInitialResultStreaming,
    onStopStream,
}) => {
    const { block, element } = useClassnames(CLASSNAME);
    const { translateKey } = useTranslate();

    const { ref: sentinelRef, inView: sentinelInView } = useIsInView({ threshold: 0 });

    const footerIsSticky = !sentinelInView && Boolean(isOpen);

    const buttonProps: ButtonProps = {
        children: isStreamingOrLoading ? undefined : translateKey(GLOBAL.SUBMIT),
        'aria-label': isStreamingOrLoading ? translateKey(ASK_AI_CONVERSATION.STOP_STREAMING) : undefined,
        leftIcon: isStreamingOrLoading ? mdiStop : mdiArrowUp,
        onClick: isStreamingOrLoading ? onStopStream : undefined,
        className: element('send-button', { loading: isStreamingOrLoading ?? false }),
        ref: buttonRef,
        isDisabled: isInitialResultStreaming,
    };

    return (
        <>
            <div className={block({ sticky: footerIsSticky, open: Boolean(isOpen) })}>
                <FlexBox orientation={Orientation.vertical} gap={Size.big} className={element('background')}>
                    <FlexBox orientation={Orientation.vertical} gap={Size.big}>
                        <AskAiFollowUpInput
                            className={element('input')}
                            onSubmit={onSubmit}
                            buttonProps={buttonProps}
                            textFieldProps={{
                                placeholder: translateKey(ASK_AI.ASK_FOR_MORE_DETAILS),
                                inputRef,
                                isDisabled: isStreamingOrLoading,
                            }}
                        />
                    </FlexBox>
                    {isOpen && (
                        <FlexBox
                            orientation={Orientation.horizontal}
                            hAlign={Alignment.center}
                            vAlign={Alignment.spaceEvenly}
                            className={element('close-follow-up-button')}
                        >
                            <Button
                                emphasis={Emphasis.low}
                                leftIcon={mdiClose}
                                onClick={onClose}
                                isDisabled={isInitialResultStreaming}
                                size={Size.m}
                            >
                                {translateKey(ASK_AI.CLOSE_FOLLOW_UP_QUESTIONS)}
                            </Button>
                        </FlexBox>
                    )}
                </FlexBox>
            </div>
            {/* Intersection sentinel to know if the footer is currently sticky or not */}
            <div ref={sentinelRef} />
        </>
    );
};
